import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Alert } from 'react-bootstrap';
import {
  branch,
  compose,
  getContext,
  lifecycle,
  renderComponent,
} from 'recompose';
import fictionIcon from '../common/images/icons/ic_binder_books.png';
import goodDealIcon from '../common/images/icons/ic_binder_gooddeal.png';
import articleIcon from '../common/images/icons/ic_binder_news.png';
import appIcon from '../common/images/icons/ic_filetype_app.png';
import bookmarkIcon from '../common/images/icons/ic_filetype_bookmark.png';
import ebookIcon from '../common/images/icons/ic_filetype_ebook.png';
import excelIcon from '../common/images/icons/ic_filetype_excel.png';
import fileIcon from '../common/images/icons/ic_filetype_file.png';
import folderIcon from '../common/images/icons/ic_filetype_folder.png';
import musicIcon from '../common/images/icons/ic_filetype_music.png';
import noteIcon from '../common/images/icons/ic_filetype_note.png';
import pdfIcon from '../common/images/icons/ic_filetype_pdf.png';
import photoIcon from '../common/images/icons/ic_filetype_photo.png';
import powerpointIcon from '../common/images/icons/ic_filetype_powerpoint.png';
import videoIcon from '../common/images/icons/ic_filetype_video.png';
import wordIcon from '../common/images/icons/ic_filetype_word.png';
import { ZODIAC_SIGNS } from '../core/constants';
import { Permissions } from '../routes/user/UserPermissions';
import LoadingProgress from './LoadingProgress/LoadingProgress';

function formatDAPO(order) {
  moment.locale('fr');
  if (order.cancelled === true) {
    return 'Annulé';
  }
  if (order.dapo != null) {
    if (order.dapo.sent === true) {
      if (order.dapo.confirmed === true) {
        if (order.dapo.confirmedTimestamp) {
          return `Expédié (${moment(order.dapo.confirmedTimestamp).format(
            'L',
          )})`;
        }
        return `Expédié`;
      }
      return 'En cours';
    }
    return 'À préparer';
  }
  return 'Inconnu';
}

function formatDelivery(order) {
  moment.locale('fr');
  if (order.cancelled === true) {
    return 'Annulé';
  }
  if (order.dapo == null || order.dapo.confirmed !== true) {
    return 'Attente expédition';
  }
  if (order.delivery != null) {
    if (order.delivery.delivered === true) {
      if (order.delivery.deliveryTimestamp) {
        return `Livré (${order.delivery.trackingNumber}, ${moment(
          order.delivery.deliveryTimestamp,
        ).format('L')})`;
      }
      return `Livré (${order.delivery.trackingNumber})`;
    } else if (order.delivery.error === true) {
      return `Erreur (${order.delivery.trackingNumber})`;
    }
    return `En cours (${order.delivery.trackingNumber})`;
  }
  return 'Inconnu';
}

function formatFSPlus(order) {
  moment.locale('fr');
  if (order.cancelled === true) {
    return 'Annulé';
  }
  if (order.postmanInstallation === false) {
    return 'Sans installation';
  } else if (order.dapo == null || order.dapo.confirmed !== true) {
    if (order.fsPlus != null && order.fsPlus.sent === true) {
      return 'Ne pas faire de demande';
    }
    return `Attente expédition (${order.postmanInstallationLabel})`;
  } else if (order.fsPlus != null) {
    if (order.fsPlus.completed === true) {
      if (
        order.fsPlus.missionCompleted &&
        order.fsPlus.missionCompleted.completedTimestamp
      ) {
        return `Effectué (${order.postmanInstallationLabel}, ${moment(
          order.fsPlus.missionCompleted.completedTimestamp,
        ).format('L')})`;
      }
      return `Effectué (${order.postmanInstallationLabel})`;
    } else if (order.fsPlus.error === true) {
      return `Erreur (${order.postmanInstallationLabel})`;
    } else if (order.fsPlus.sent === false) {
      return `À commander (${order.postmanInstallationLabel})`;
    }
    return `En cours (${order.postmanInstallationLabel})`;
  }
  return 'Inconnu';
}

function formatAddress(address) {
  if (address !== null) {
    return `${address.firstName || ''} ${address.lastName || ''}\n${
      address.addressLine || ''
    }\n${address.zipCode || ''} ${address.city || ''}`;
  }
  return 'NA';
}

function tsToDateForInput(ts, withoutHours) {
  moment.locale('fr');
  if (typeof ts !== 'number') {
    return '-';
  }
  if (withoutHours) {
    return moment(ts).format('L');
  }
  return `${moment(ts).format('L')} ${moment(ts).format('LT')}`;
}

const Loading = () => <LoadingProgress show />;

const withLoadingComponent = (loadingComp) =>
  branch(
    (props) => props.data.loading,
    renderComponent(loadingComp || Loading),
  );

const withAlertContainer = getContext({
  successMsg: PropTypes.func,
  errorMsg: PropTypes.func,
});

const withSelectedLink = getContext({
  selectedLink: PropTypes.object,
  partnerNumber: PropTypes.string,
});

const setPage = (page) =>
  compose(
    getContext({
      handleCurrentPageChanged: PropTypes.func,
    }),
    lifecycle({
      componentDidMount() {
        this.props.handleCurrentPageChanged(page);
      },
    }),
  );

const setPageFromProps = compose(
  getContext({
    handleCurrentPageChanged: PropTypes.func,
  }),
  lifecycle({
    componentDidMount() {
      this.props.handleCurrentPageChanged(this.props.page);
    },
    componentDidUpdate(previousProps) {
      if (previousProps.page !== this.props.page) {
        this.props.handleCurrentPageChanged(this.props.page);
      }
    },
  }),
);

const withPage = getContext({
  currentPage: PropTypes.object,
});

const Error = (props) => {
  useEffect(() => console.error('Error:', props.data?.error));
  return <div>Error...</div>;
};

const ErrorAlertComponent = ({ data }) => {
  const { message } = data.error;
  let messageToShow = 'Une erreur inconnue est survenue';

  if (message.indexOf('Unsatisfied permission(s)') !== -1) {
    messageToShow =
      "Vous n'avez pas les droits nécessaires pour afficher ce contenu";
  }

  return (
    <Alert bsStyle="danger" className="text-center">
      {messageToShow}
    </Alert>
  );
};

const withErrorComponent = (errorComp) =>
  branch((props) => props.data.error, renderComponent(errorComp || Error));

const convertPermissionForTab = (type) => Permissions.labelOf(type, type);

const documentDict = {
  APP: {
    nameForTab: 'Application',
    documentIcon: appIcon,
    miniIcon: 'android',
  },
  INFO: {
    nameForTab: 'Application',
    documentIcon: appIcon,
    miniIcon: 'android',
  },
  BOOKMARK: {
    nameForTab: 'Favori internet',
    documentIcon: bookmarkIcon,
    miniIcon: 'link',
  },
  BOOK: { nameForTab: 'Livre', documentIcon: ebookIcon, miniIcon: 'book' },
  EXCEL: {
    nameForTab: 'Classeur Excel',
    documentIcon: excelIcon,
    miniIcon: 'file-excel-o',
  },
  FOLDER: {
    nameForTab: 'Dossier',
    documentIcon: folderIcon,
    miniIcon: 'folder-open-o',
  },
  MUSIC: {
    nameForTab: 'Audio',
    documentIcon: musicIcon,
    miniIcon: 'file-audio-o',
  },
  NOTE: {
    nameForTab: 'Note',
    documentIcon: noteIcon,
    miniIcon: 'pencil-square-o',
  },
  PDF: {
    nameForTab: 'Document PDF',
    documentIcon: pdfIcon,
    miniIcon: 'file-pdf-o',
  },
  PICTURE: {
    nameForTab: 'Image',
    documentIcon: photoIcon,
    miniIcon: 'file-image-o',
  },
  POWERPOINT: {
    nameForTab: 'Présentation Powerpoint',
    documentIcon: powerpointIcon,
    miniIcon: 'file-powerpoint-o',
  },
  VIDEO: {
    nameForTab: 'Vidéo',
    documentIcon: videoIcon,
    miniIcon: 'file-video-o',
  },
  WORD: {
    nameForTab: 'Document Word',
    documentIcon: wordIcon,
    miniIcon: 'file-word-o',
  },
};

const contentDict = {
  ARTICLE: {
    nameForTab: 'Article',
    documentIcon: articleIcon,
    miniIcon: 'file-text-o',
  },
  DAILY_NEWS: {
    nameForTab: 'Article',
    documentIcon: articleIcon,
    miniIcon: 'file-text-o',
  },
  EMAG: {
    nameForTab: 'Magazine',
    documentIcon: articleIcon,
    miniIcon: 'newspaper-o',
  },
  FICTION: {
    nameForTab: 'Fiction',
    documentIcon: fictionIcon,
    miniIcon: 'book',
  },
  GOOD_DEAL: {
    nameForTab: 'Bon plan',
    documentIcon: goodDealIcon,
    miniIcon: 'tag',
  },
  HOROSCOPE: {
    nameForTab: 'Article',
    documentIcon: fictionIcon,
    miniIcon: 'newspaper-o',
  },
  QUIZ: {
    nameForTab: 'Quiz',
    documentIcon: noteIcon,
    miniIcon: 'question-circle-o',
  },
};

const zodiacImages = import.meta.glob(
  '../routes/content/horoscope/images/*.png',
  { eager: true, import: 'default' },
);

const contentTitleDict = {};
ZODIAC_SIGNS.forEach((sign) => {
  contentTitleDict[sign.title] = {
    documentIcon:
      zodiacImages[`../routes/content/horoscope/images/${sign.name}.png`],
  };
});

const notificationDict = {
  LINK: {
    nameForTab: 'Lien internet',
    documentIcon: fileIcon,
    miniIcon: 'link',
  },
  WITHOUT: {
    nameForTab: 'Sans action',
    documentIcon: fileIcon,
    miniIcon: 'info-circle',
  },
};

const convertNameForTab = (type) =>
  documentDict[type]?.nameForTab ||
  contentDict[type]?.nameForTab ||
  notificationDict[type]?.nameForTab ||
  type;

const getDocumentIcon = (document, switchProp) => {
  let type = 'UNKNOWN';

  if (document) {
    type = switchProp ? document[switchProp] : document.type;
  }

  return (
    documentDict[type]?.documentIcon ||
    (contentDict[type]?.documentIcon
      ? getContentIcon(document, type)
      : notificationDict[type]?.documentIcon || fileIcon)
  );
};

const getContentIcon = (document, type) => {
  let iconSrc = contentDict[type].documentIcon;

  if (document.title && contentTitleDict[document.title]) {
    iconSrc = contentTitleDict[document.title].documentIcon;
  }

  return iconSrc;
};

const getDocumentMiniIcon = (type) =>
  documentDict[type]?.miniIcon ||
  contentDict[type]?.miniIcon ||
  notificationDict[type]?.miniIcon ||
  'file-o';

const EMAIL_REGEX =
  '^(([^<>()\\[\\]\\\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$';
const emailRegexObj = RegExp(EMAIL_REGEX);
const emailValidator = (email) => emailRegexObj.test(email);

const setIn = (obj, path, value) =>
  _.setWith(_.clone(obj), path, value, _.clone);

export {
  tsToDateForInput,
  Loading,
  withLoadingComponent,
  ErrorAlertComponent,
  withErrorComponent,
  convertPermissionForTab,
  convertNameForTab,
  getDocumentIcon,
  getDocumentMiniIcon,
  formatDAPO,
  formatDelivery,
  formatFSPlus,
  formatAddress,
  withAlertContainer,
  withSelectedLink,
  setPage,
  setPageFromProps,
  withPage,
  EMAIL_REGEX,
  emailValidator,
  setIn,
};
